import { useEffect, useRef, useState } from "react";
import {
  Box, Flex, Text, Input, InputGroup, InputLeftElement, Select, Popover, PopoverTrigger, PopoverContent, PopoverBody, useDisclosure, Spinner, Center, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { CustomTable, CustomToast } from "../../../../components";
import { useFetch } from "../../../../hooks/useFetch";
import { Column } from "../../../../components/CustomTable/customTable";
import { showDate, showDateTime } from "../../../../utilities/helper";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

type DayRow = {
  date: string;
  day: string;
  inTime: string;
  outTime: string;
  statusName: string;
  isWeekOff: boolean;
  totalBreakTime: string;
  breakDetails: any[];
  source?: string;
  punchLogs?: { punchTime: string; punchType: string }[];
};

export type EmployeeSummary = {
  totalDays: number; present: number; absent: number; halfDay: number; late: number;
  earlyOut: number; punchMissing: number; weekOff: number; shortLeave: number; slHalfDay: number;
};

const statusStyles: Record<string, { bg: string; color: string }> = {
  present: { bg: "#dcfce7", color: "#166534" },
  absent: { bg: "#fee2e2", color: "#991b1b" },
  "half day": { bg: "#fef3c7", color: "#92400e" },
  late: { bg: "#fef9c3", color: "#854d0e" },
  earlyout: { bg: "#e0f2fe", color: "#075985" },
  punchinmissing: { bg: "#f3e8ff", color: "#6b21a8" },
  punchoutmissing: { bg: "#fae8ff", color: "#86198f" },
  "week off": { bg: "#f0fdf4", color: "#166534" },
};

const sourceStyles: Record<string, { bg: string; color: string }> = {
  regularization: { bg: "#f3e8ff", color: "#6b21a8" },
  manual: { bg: "#ffedd5", color: "#c2410c" },
  remote: { bg: "#e0f2fe", color: "#0369a1" },
  automatic: { bg: "#dcfce7", color: "#166534" },
};

const buildSummaryFromDays = (rows: DayRow[]): EmployeeSummary => {
  const s: EmployeeSummary = {
    totalDays: rows.length, present: 0, absent: 0, halfDay: 0, late: 0,
    earlyOut: 0, punchMissing: 0, weekOff: 0, shortLeave: 0, slHalfDay: 0,
  };
  rows.forEach((r) => {
    const name = (r.statusName || "").toLowerCase();
    if (r.isWeekOff || name === "week off") s.weekOff++;
    else if (name === "present") s.present++;
    else if (name === "absent") s.absent++;
    else if (name === "half day") s.halfDay++;
    else if (name === "late") s.late++;
    else if (name === "earlyout") s.earlyOut++;
    else if (name === "punchinmissing" || name === "punchoutmissing") s.punchMissing++;
    else if (name === "short leave") s.shortLeave++;
    else if (name === "sl-half day" || name === "sl half day") s.slHalfDay++;
  });
  return s;
};

const EmployeeWiseAttendanceList = ({ onSummaryChange }: { onSummaryChange?: (s: EmployeeSummary | null) => void }) => {
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const now = new Date();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null);
  const [year, setYear] = useState<number>(now.getFullYear());
  const [month, setMonth] = useState<number>(now.getMonth() + 1);
  const [days, setDays] = useState<DayRow[]>([]);

  const [breakModalOpen, setBreakModalOpen] = useState(false);
  const [selectedBreaks, setSelectedBreaks] = useState<any[]>([]);
  const [selectedBreakDate, setSelectedBreakDate] = useState("");
  const [punchModalOpen, setPunchModalOpen] = useState(false);
  const [selectedPunchLogs, setSelectedPunchLogs] = useState<any[]>([]);
  const [selectedPunchDate, setSelectedPunchDate] = useState("");

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const yearOptions = Array.from({ length: 6 }, (_, i) => now.getFullYear() - i);

  const isFutureMonth = (y: number, m: number) => new Date(y, m - 1, 1) > new Date(now.getFullYear(), now.getMonth(), 1);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (selectedEmployee) return;
    if (!searchTerm.trim()) {
      setSearchResults([]);
      onClose();
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      const data = await fetchApi("Hrlense_Attendance/searchEmployee", "GET", null, `searchTerm=${encodeURIComponent(searchTerm)}`);
      setSearching(false);
      if (data) {
        setSearchResults(data);
        onOpen();
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm, selectedEmployee]);

  useEffect(() => {
    if (selectedEmployee) fetchMonthData();
  }, [selectedEmployee, year, month]);

  const fetchMonthData = async () => {
    const data = await fetchApi("Hrlense_Attendance/employeeWiseAttendanceList", "GET", null,
      `employeeId=${selectedEmployee.id}&year=${year}&month=${month}`
    );
    if (data) {
      const rows: DayRow[] = (data.days || []).map((d: any) => ({
        date: d.date,
        day: d.day,
        inTime: d.inTime || "",
        outTime: d.outTime || "",
        statusName: d.statusName,
        isWeekOff: d.isWeekOff || false,
        totalBreakTime: d.totalBreakTime || "",
        breakDetails: d.breakDetails || [],
        punchLogs: d.punchLogs || [],
      }));
      setDays(rows);
      onSummaryChange?.(data.summary ?? buildSummaryFromDays(rows));
    }
  };

  const handleSelectEmployee = (emp: any) => {
    setSelectedEmployee(emp);
    setSearchTerm(`${emp.emp_Name} (${emp.user_Employee_id})`);
    setSearchResults([]);
    onClose();
  };

  const dash = (v: string) => (!v || v === "00:00:00" || v === "00:00" ? "—" : v);

  const openPunchLogs = (row: DayRow) => {
    setSelectedPunchDate(row.date);
    setSelectedPunchLogs(row.punchLogs || []);
    setPunchModalOpen(true);
  };

  return (
    <Box>
      <Flex gap={4} mb={4} align="center" wrap="wrap">
        <Popover isOpen={isOpen} onClose={onClose} autoFocus={false} placement="bottom-start" matchWidth>
          <PopoverTrigger>
            <InputGroup maxW="280px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input placeholder="Search employee by name" value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedEmployee(null);
                  setDays([]);
                  onSummaryChange?.(null);
                }}
                onFocus={() => searchResults.length > 0 && onOpen()}
              />
            </InputGroup>
          </PopoverTrigger>
          <PopoverContent w="280px">
            <PopoverBody p={1} maxH="240px" overflowY="auto">
              {searching ? (
                <Flex justify="center" py={2}><Spinner size="sm" /></Flex>
              ) : searchResults.length > 0 ? (
                searchResults.map((emp) => (
                  <Box key={emp.id} px={3} py={2} borderRadius="md" cursor="pointer" _hover={{ bg: "gray.100" }} onClick={() => handleSelectEmployee(emp)}>
                    <Text className="font-poppins text_sm text_medium">{emp.emp_Name}</Text>
                    <Text className="font-poppins text_xs" color="gray.500">{emp.user_Employee_id} · {emp.company_Name}</Text>
                  </Box>
                ))
              ) : (
                <Text px={3} py={2} className="font-poppins text_sm" color="gray.500">No matches</Text>
              )}
            </PopoverBody>
          </PopoverContent>
        </Popover>

        <Select maxW="140px" value={month} onChange={(e) => setMonth(Number(e.target.value))} isDisabled={!selectedEmployee}>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1} disabled={isFutureMonth(year, i + 1)}>{m}</option>
          ))}
        </Select>

        <Select maxW="110px" value={year}
          onChange={(e) => {
            const newYear = Number(e.target.value);
            setYear(newYear);
            if (isFutureMonth(newYear, month)) setMonth(now.getMonth() + 1);
          }}
          isDisabled={!selectedEmployee}
        >
          {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </Select>
      </Flex>

      {selectedEmployee ? (
        <>
          <Text className="font-poppins text_md text_medium" mb={3}>
            {selectedEmployee.emp_Name} – {MONTHS[month - 1]} {year}
          </Text>
          <Box position="relative" minH="300px">
            {loading ? (
              <Center position="absolute" top={0} left={0} right={0} bottom={0}>
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="#006838" size="xl" />
              </Center>
            ) : (
              <CustomTable value={days} emptyMessage="No Data Available">
                <Column header="S. No" body={(_, index) => index + 1} />
                <Column header="Date" field="date" body={(row: DayRow) => (
                  <Text as="span" color="#006838" fontWeight="500" textDecoration="underline" cursor="pointer"
                    onClick={() => openPunchLogs(row)}
                  >
                    {showDate(row.date)}
                  </Text>
                )} />
                <Column header="Day" field="day" />
                <Column header="Check IN" body={(row: DayRow) => row.inTime ? dash(row.inTime) : (row.isWeekOff ? "N/A" : "—") } />
                <Column header="Check OUT" body={(row: DayRow) => row.outTime ? dash(row.outTime) : (row.isWeekOff ? "N/A" : "—") } />
                <Column header="Break Time" body={(row: DayRow) => !row.totalBreakTime ? ("N/A") : (
                  <Badge colorScheme="orange" cursor="pointer" px={3} py={1} borderRadius="md"
                    onClick={() => {
                      setSelectedBreaks(row.breakDetails || []); setSelectedBreakDate(row.date); setBreakModalOpen(true);
                    }}
                  >
                    {row.totalBreakTime}
                  </Badge>
                )
                }
                />
                <Column header="Status" body={(row: DayRow) => {
                  const key = row.statusName?.toLowerCase() || "";
                  const { bg, color } = statusStyles[key] || { bg: "#f1f5f9", color: "#475569" };
                  return (
                    <Box px={3} py={1} borderRadius="md" textAlign="center" fontSize="sm" fontWeight="500" bg={bg} color={color} className="font-poppins">
                      {row.statusName || "N/A"}
                    </Box>
                  );
                }}
                />
                <Column header="Source" body={(row: DayRow) => {
                  const src = row.source || "Automatic";
                  const key = src.toLowerCase();
                  const { bg, color } = sourceStyles[key] || { bg: "#dcfce7", color: "#166534" };
                  return (
                    <Box px={3} py={1} borderRadius="md" textAlign="center" fontSize="sm" fontWeight="500" bg={bg} color={color} className="font-poppins">
                      {src}
                    </Box>
                  );
                }}
                />
              </CustomTable>
            )}
          </Box>
        </>
      ) : (
        <Text className="font-poppins text_sm" color="gray.500">
          Search and select an employee, then choose month and year to view attendance.
        </Text>
      )}

      <Modal isOpen={breakModalOpen} onClose={() => { setBreakModalOpen(false); setSelectedBreaks([]); }} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader mt={4}>Break Details – {selectedEmployee?.emp_Name} ({showDate(selectedBreakDate)})</ModalHeader>
          <ModalBody pb={5}>
            <CustomTable value={selectedBreaks} loading={false} emptyMessage="No break details found.">
              <Column header="S. No" body={(_, index) => index + 1} />
              <Column header="Out Time" body={(row: any) => showDateTime(row.outTime)} />
              <Column header="In Time" body={(row: any) => showDateTime(row.inTime)} />
              <Column header="Break Duration" body={(row: any) => row.breakDuration} />
              <Column header="Minutes" body={(row: any) => row.totalMinutes} />
            </CustomTable>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={punchModalOpen} onClose={() => { setPunchModalOpen(false); setSelectedPunchLogs([]); setSelectedPunchDate(""); }} size="lg" isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader mt={4}> Punch Logs – {showDate(selectedPunchDate)} </ModalHeader>
          <ModalBody pb={5}>
            <CustomTable value={selectedPunchLogs} loading={false} emptyMessage="No punches found for this date." >
              <Column header="S. No" body={(_, index) => index + 1} />
              <Column header="Punch Time" body={(log: any) => showDateTime(log.punchTime)} />
              <Column header="Punch Type" body={(log: any) => (<Badge colorScheme={log.punchType?.toUpperCase() === "IN" ? "green" : "red"} > {log.punchType?.toUpperCase()}</Badge>)} />
            </CustomTable>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default EmployeeWiseAttendanceList;